import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Baby, CalendarDays, Loader2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../lib/auth-context';

const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const today = () => new Date().toISOString().slice(0, 10);

/**
 * V17 — "Has your baby arrived?" prompt. Shown from EDD day onward while the
 * account is still prenatal (backend `state.delivery_prompt.show`).
 *  Yes      → delivery date + mode → account flips to postnatal (Baby Tracker opens).
 *  Not yet  → ONE due-date change of at most ±10 days, else contact support; or snooze 24h.
 */
export default function DeliveryCheckDialog({ prompt, onUpdated }) {
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('ask');
  const [saving, setSaving] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(today());
  const [deliveryMode, setDeliveryMode] = useState('');
  const [babyName, setBabyName] = useState('');
  const [newEdd, setNewEdd] = useState('');

  useEffect(() => {
    if (prompt?.show) { setOpen(true); setStep('ask'); }
    else setOpen(false);
  }, [prompt?.show]);

  if (!prompt?.show) return null;

  const post = async (body) => api.post('/api/learner/me/delivery-check', body);

  const submitDelivered = async () => {
    if (!deliveryMode) return;
    setSaving(true);
    try {
      await post({ delivered: true, delivery_date: deliveryDate, delivery_mode: deliveryMode, baby_name: babyName || undefined });
      toast.success('Congratulations, mama! Your postnatal journey starts now 💛');
      setOpen(false);
      await refreshUser();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save — please try again');
    } finally { setSaving(false); }
  };

  const submitNewEdd = async () => {
    if (!newEdd) return;
    setSaving(true);
    try {
      const r = await post({ delivered: false, new_edd: newEdd });
      toast.success(`Due date updated to ${fmt(r.data.edd)}`);
      setOpen(false);
      await refreshUser();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update your due date');
    } finally { setSaving(false); }
  };

  const snooze = async () => {
    setSaving(true);
    try { await post({ delivered: false }); } catch (e) { /* best-effort */ }
    setSaving(false);
    setOpen(false);
    onUpdated?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) snooze(); }}>
      <DialogContent className="sm:max-w-md" data-testid="delivery-check-dialog">
        {step === 'ask' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Baby className="h-5 w-5 text-rose-500" /> Has your baby arrived?</DialogTitle>
              <DialogDescription data-testid="delivery-check-edd">
                {prompt.message
                  || `Your due date was ${fmt(prompt.edd)}${prompt.days_past_edd > 0 ? ` — ${prompt.days_past_edd} day${prompt.days_past_edd === 1 ? '' : 's'} ago` : ' (today)'}. Tell us so we can switch you to postnatal care and open your Baby Tracker.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button className="w-full rounded-full bg-rose-500 hover:bg-rose-600" onClick={() => setStep('yes')} data-testid="delivery-check-yes">Yes, my baby is here</Button>
              <Button variant="outline" className="w-full rounded-full" onClick={() => setStep('notyet')} data-testid="delivery-check-no">Not yet</Button>
              <button type="button" className="text-xs text-muted-foreground underline underline-offset-2 mx-auto" onClick={snooze} disabled={saving} data-testid="delivery-check-snooze">Remind me tomorrow</button>
            </DialogFooter>
          </>
        )}

        {step === 'yes' && (
          <>
            <DialogHeader>
              <DialogTitle>Congratulations, mama!</DialogTitle>
              <DialogDescription>A couple of details so your postnatal classes are safe for your recovery.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="dc-date">Delivery date</Label>
                <Input id="dc-date" type="date" max={today()} value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="delivery-check-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Delivery type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[['vaginal', 'Vaginal'], ['c_section', 'C-section']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setDeliveryMode(v)} data-testid={`delivery-check-mode-${v}`}
                      className={`rounded-xl border px-3 py-2 text-sm transition-colors ${deliveryMode === v ? 'border-rose-400 bg-rose-50 text-rose-700' : 'border-border hover:bg-muted'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dc-name">Baby's name <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="dc-name" value={babyName} onChange={(e) => setBabyName(e.target.value)} placeholder="You can add this later" data-testid="delivery-check-baby-name" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep('ask')} disabled={saving} data-testid="delivery-check-back">Back</Button>
              <Button className="rounded-full bg-rose-500 hover:bg-rose-600" onClick={submitDelivered} disabled={!deliveryMode || !deliveryDate || saving} data-testid="delivery-check-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & switch to postnatal'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'notyet' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-amber-500" /> Still waiting for baby</DialogTitle>
              <DialogDescription>
                {prompt.can_adjust_edd
                  ? `You can move your due date once, by up to ${prompt.max_shift_days} days.${prompt.reason === 'edd_reached' ? " We'll switch you to postnatal automatically 3 days after it." : ' We\'ll put your prenatal plan back once you do.'}`
                  : 'Your due date has already been adjusted once (or is too far back to adjust here). Please contact our team and we\'ll sort it out.'}
              </DialogDescription>
            </DialogHeader>
            {prompt.can_adjust_edd ? (
              <div className="space-y-1.5">
                <Label htmlFor="dc-edd">New due date</Label>
                <Input id="dc-edd" type="date" min={prompt.min_new_edd} max={prompt.max_new_edd} value={newEdd} onChange={(e) => setNewEdd(e.target.value)} data-testid="delivery-check-new-edd" />
                <p className="text-xs text-muted-foreground">Allowed: {fmt(prompt.min_new_edd)} – {fmt(prompt.max_new_edd)}</p>
              </div>
            ) : (
              <a href={prompt.contact_support_url} className="inline-flex items-center gap-2 text-sm text-rose-600 underline underline-offset-2" data-testid="delivery-check-contact-support">
                <LifeBuoy className="h-4 w-4" /> Contact our team
              </a>
            )}
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep('ask')} disabled={saving} data-testid="delivery-check-back-2">Back</Button>
              <Button variant="outline" className="rounded-full" onClick={snooze} disabled={saving} data-testid="delivery-check-keep-edd">Keep my due date</Button>
              {prompt.can_adjust_edd && (
                <Button className="rounded-full" onClick={submitNewEdd} disabled={!newEdd || saving} data-testid="delivery-check-save-edd">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update due date'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
